import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Eye, EyeOff } from 'lucide-react';
import { Button, Card, CardTitle, Field, Input } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import { passwordChangeSchema, type PasswordChangeInput } from '../schemas';
import { useChangePassword } from '../api';

interface Props {
  userId: string;
}

interface Strength {
  len: boolean;
  upper: boolean;
  num: boolean;
  score: 0 | 1 | 2 | 3;
}

function strengthOf(pw: string): Strength {
  const len = pw.length >= 8;
  const upper = /[A-Z]/.test(pw);
  const num = /\d/.test(pw);
  const score = (Number(len) + Number(upper) + Number(num)) as 0 | 1 | 2 | 3;
  return { len, upper, num, score };
}

const SCORE_LABEL: Record<Strength['score'], string> = {
  0: 'Fraca',
  1: 'Fraca',
  2: 'Média',
  3: 'Forte',
};

export function SenhaForm({ userId }: Props) {
  const mutation = useChangePassword(userId);
  const [show, setShow] = useState({ current: false, next: false, conf: false });

  const form = useForm<PasswordChangeInput>({
    resolver: zodResolver(passwordChangeSchema),
    defaultValues: { current_password: '', new_password: '', confirm_password: '' },
  });

  const newPw = form.watch('new_password');
  const strength = strengthOf(newPw);

  const onSubmit = form.handleSubmit((values) => {
    mutation.mutate(values, {
      onSuccess: () =>
        form.reset({ current_password: '', new_password: '', confirm_password: '' }),
    });
  });

  return (
    <Card padding="lg">
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        <CardTitle>Segurança · senha</CardTitle>
        <Stack gap="md">
          <Field
            label="Senha atual"
            htmlFor="perfil-senha-atual"
            error={form.formState.errors.current_password?.message}
            required
          >
            <PasswordField
              id="perfil-senha-atual"
              show={show.current}
              onToggle={() => setShow((s) => ({ ...s, current: !s.current }))}
              register={form.register('current_password')}
              invalid={!!form.formState.errors.current_password}
              autoComplete="current-password"
            />
          </Field>

          <Field
            label="Nova senha"
            htmlFor="perfil-nova-senha"
            error={form.formState.errors.new_password?.message}
            required
          >
            <PasswordField
              id="perfil-nova-senha"
              show={show.next}
              onToggle={() => setShow((s) => ({ ...s, next: !s.next }))}
              register={form.register('new_password')}
              invalid={!!form.formState.errors.new_password}
              autoComplete="new-password"
            />
            <StrengthMeter strength={strength} />
          </Field>

          <Field
            label="Confirmar nova senha"
            htmlFor="perfil-conf-senha"
            error={form.formState.errors.confirm_password?.message}
            required
          >
            <PasswordField
              id="perfil-conf-senha"
              show={show.conf}
              onToggle={() => setShow((s) => ({ ...s, conf: !s.conf }))}
              register={form.register('confirm_password')}
              invalid={!!form.formState.errors.confirm_password}
              autoComplete="new-password"
            />
          </Field>
        </Stack>

        {mutation.error && (
          <p className="text-xs text-danger">
            {mutation.error instanceof ApiError ? mutation.error.message : 'Falha ao trocar senha'}
          </p>
        )}
        {mutation.isSuccess && <p className="text-xs text-ok">Senha alterada com sucesso.</p>}

        <div className="flex justify-end gap-2">
          <Button type="submit" variant="primary" disabled={mutation.isPending}>
            {mutation.isPending ? 'Alterando…' : 'Alterar senha'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

interface PasswordFieldProps {
  id: string;
  show: boolean;
  onToggle: () => void;
  register: ReturnType<ReturnType<typeof useForm<PasswordChangeInput>>['register']>;
  invalid?: boolean;
  autoComplete?: string;
}

function PasswordField({ id, show, onToggle, register, invalid, autoComplete }: PasswordFieldProps) {
  return (
    <div className="relative">
      <Input
        id={id}
        type={show ? 'text' : 'password'}
        autoComplete={autoComplete ?? 'new-password'}
        invalid={invalid ?? false}
        className="pr-10"
        {...register}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? 'Esconder senha' : 'Mostrar senha'}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-text-muted hover:text-text"
      >
        {show ? <EyeOff size={15} strokeWidth={1.5} /> : <Eye size={15} strokeWidth={1.5} />}
      </button>
    </div>
  );
}

function StrengthMeter({ strength }: { strength: Strength }) {
  const segs: Array<'on' | 'off'> = [
    strength.score >= 1 ? 'on' : 'off',
    strength.score >= 2 ? 'on' : 'off',
    strength.score >= 3 ? 'on' : 'off',
  ];
  const tone =
    strength.score === 3
      ? 'bg-ok'
      : strength.score === 2
        ? 'bg-warning'
        : 'bg-danger';

  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex gap-1">
        {segs.map((s, i) => (
          <span
            key={i}
            className={`h-1 flex-1 rounded-full ${s === 'on' ? tone : 'bg-surface3'}`}
          />
        ))}
      </div>
      <div className="flex items-center justify-between text-[10px] text-text-muted">
        <span>{SCORE_LABEL[strength.score]}</span>
        <span>
          {strength.len ? '✓' : '·'} 8+ chars · {strength.upper ? '✓' : '·'} maiúscula ·{' '}
          {strength.num ? '✓' : '·'} número
        </span>
      </div>
    </div>
  );
}
